// custom-ui/IconChip.tsx
// The single rounded icon container used across the app (list rows, section
// headers, empty states). Replaces the ad-hoc circles (solid-teal vs light-green
// vs rounded-square) with one consistent treatment. Default = sage brand tint.
import React from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

type IconChipSize = 'sm' | 'md' | 'lg';
type IconChipTone = 'brand' | 'neutral' | 'green' | 'amber' | 'red' | 'blue';

const SIZES: Record<IconChipSize, { box: string; glyph: string }> = {
    sm: { box: 'w-9 h-9', glyph: 'w-4 h-4' },
    md: { box: 'w-10 h-10', glyph: 'w-5 h-5' },
    lg: { box: 'w-12 h-12', glyph: 'w-6 h-6' },
};

const TONES: Record<IconChipTone, string> = {
    brand: 'bg-[#A4C3B2]/30 text-[#1A4D4F]',
    neutral: 'bg-gray-100 text-gray-500',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    blue: 'bg-blue-50 text-blue-600',
};

interface IconChipProps {
    icon: LucideIcon;
    size?: IconChipSize;
    tone?: IconChipTone;
    className?: string;
}

export const IconChip: React.FC<IconChipProps> = ({ icon: Icon, size = 'md', tone = 'brand', className }) => {
    const s = SIZES[size];
    return (
        <span className={cn('flex items-center justify-center rounded-xl flex-shrink-0', s.box, TONES[tone], className)}>
            <Icon className={s.glyph} />
        </span>
    );
};

export default IconChip;
